<template>
  <section v-if="!isLoading" class="select-outcome">
    <OutcomeModal ref="outcomeModal" :outcomeUid="selectedOutcomeUid" :outcome="selectedOutcome" />
    <div class="text-center">
      <h1 class="outcome-page-header" data-test="pg-page-title">Choose your Outcome</h1>
      <h2 class="outcome-subtitle">I want to be more...</h2>
    </div>
    <!--    <div class="outcome-grid" data-test="pg-outcome">-->
    <transition-group
      tag="div"
      class="outcome-grid"
      appear
      @before-enter="beforeEnter"
      @enter="enter"
      data-test="pg-outcome"
    >
      <div
        class="grid-tile"
        @click="personalOutcomes.entrepreneurial && openModal(personalOutcomes.entrepreneurial)"
        @keydown.enter="
          personalOutcomes.entrepreneurial && openModal(personalOutcomes.entrepreneurial)
        "
        key="entrepreneurial"
        data-index="1"
        tabindex="0"
      >
        <OutcomeTile
          :outcome="personalOutcomes.entrepreneurial?.data"
          data-test="pg-entrepreneurial-tile"
        />
      </div>
      <div
        class="grid-tile"
        @click="personalOutcomes.innovative && openModal(personalOutcomes.innovative)"
        @keydown.enter="personalOutcomes.innovative && openModal(personalOutcomes.innovative)"
        key="innovative"
        data-index="2"
        tabindex="0"
      >
        <OutcomeTile :outcome="personalOutcomes.innovative?.data" data-test="pg-innovative-tile" />
      </div>
      <div
        class="grid-tile"
        @click="personalOutcomes.inspirational && openModal(personalOutcomes.inspirational)"
        @keydown.enter="personalOutcomes.inspirational && openModal(personalOutcomes.inspirational)"
        key="inspirational"
        data-index="3"
        tabindex="0"
      >
        <OutcomeTile
          :outcome="personalOutcomes.inspirational?.data"
          data-test="pg-inspirational-tile"
        />
      </div>
      <div
        class="grid-tile"
        @click="personalOutcomes.resilient && openModal(personalOutcomes.resilient)"
        @keydown.enter="personalOutcomes.resilient && openModal(personalOutcomes.resilient)"
        key="resilient"
        data-index="4"
        tabindex="0"
      >
        <OutcomeTile :outcome="personalOutcomes.resilient?.data" data-test="pg-resilient-tile" />
      </div>
      <div
        class="grid-tile"
        @click="personalOutcomes.growth && openModal(personalOutcomes.growth)"
        @keydown.enter="personalOutcomes.growth && openModal(personalOutcomes.growth)"
        key="growth"
        data-index="5"
        tabindex="0"
      >
        <OutcomeTile :outcome="personalOutcomes.growth?.data" data-test="pg-growth-tile" />
      </div>
      <div
        class="grid-tile"
        @click="personalOutcomes.collaborative && openModal(personalOutcomes.collaborative)"
        @keydown.enter="personalOutcomes.collaborative && openModal(personalOutcomes.collaborative)"
        key="collaborative"
        data-index="6"
        tabindex="0"
      >
        <OutcomeTile
          :outcome="personalOutcomes.collaborative?.data"
          data-test="pg-collaborative-tile"
        />
      </div>
      <div
        class="grid-tile"
        @click="personalOutcomes.productive && openModal(personalOutcomes.productive)"
        @keydown.enter="personalOutcomes.productive && openModal(personalOutcomes.productive)"
        key="productive"
        data-index="7"
        tabindex="0"
      >
        <OutcomeTile :outcome="personalOutcomes.productive?.data" data-test="pg-productive-tile" />
      </div>
      <div
        class="grid-tile"
        @click="personalOutcomes.strategic && openModal(personalOutcomes.strategic)"
        @keydown.enter="personalOutcomes.strategic && openModal(personalOutcomes.strategic)"
        key="strategic"
        data-index="8"
        tabindex="0"
      >
        <OutcomeTile :outcome="personalOutcomes.strategic?.data" data-test="pg-strategic-tile" />
      </div>
      <div
        class="grid-tile"
        @click="personalOutcomes.compassionate && openModal(personalOutcomes.compassionate)"
        @keydown.enter="personalOutcomes.compassionate && openModal(personalOutcomes.compassionate)"
        key="compassionate"
        data-index="9"
        tabindex="0"
      >
        <OutcomeTile
          :outcome="personalOutcomes.compassionate?.data"
          data-test="pg-compassionate-tile"
        />
      </div>
    </transition-group>
  </section>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import Debug from "debug";
import { TimelineMax } from "gsap";
import { useRoute } from "vue-router";
import cms from "../../services/cms/cms";
import { CmsDocumentTypes } from "../../services/cms/cms-type-configuration";
import { CmsGoalData, CmsPersonalGoal } from "../../services/cms/cms-type-definitions";
import LoadingPage from "../../views/loading-page.vue";
import OutcomeTile from "../team-outcomes/outcome-tile.vue";
import { Analytics } from "../../services/analytics/analytics";
import { PersonalOutcomeType } from "./personal-outcome-type.enum";
import OutcomeModal from "../team-outcomes/outcome-modal.vue";

const debug = Debug("cs.PersonalOutcomes");

const route = useRoute();

const emit = defineEmits(["update-stepper"]);

const outcomeModal = ref<InstanceType<typeof OutcomeModal> | null>(null);

const outcomeData = ref<CmsPersonalGoal[] | null>(null);

const isLoading = ref(true);

const selectedOutcome = ref<CmsGoalData | null>(null);

const selectedOutcomeUid = ref<string | null>(null);

const routeName = ref<string>(route.name as string);

const personalOutcomes = ref<Record<PersonalOutcomeType, CmsPersonalGoal | null>>({
  entrepreneurial: null,
  innovative: null,
  inspirational: null,
  resilient: null,
  growth: null,
  collaborative: null,
  productive: null,
  strategic: null,
  compassionate: null,
});

const mapOutcomes = (cmsOutcomes: CmsPersonalGoal[]): void => {
  const keys = Object.values(PersonalOutcomeType);
  keys.forEach((key) => {
    personalOutcomes.value[key] = cmsOutcomes.find((g) => g.uid?.includes(key)) ?? null;
  });
};

const beforeEnter = (element: HTMLDivElement): void => {
  element.style.opacity = "0";
  element.style.transform = "translateY(50px)";
};

const enter = (element: HTMLDivElement, done: boolean): void => {
  const tl = new TimelineMax({});
  let delay: number;
  if (typeof element.dataset.index !== "undefined") {
    delay = +element.dataset.index;
    tl.to(
      element,
      0.8,
      {
        opacity: 1,
        y: 0,
        onComplete: done,
        delay: delay * 0.1,
      },
      0,
    );
  }
};

const openModal = (outcome: CmsPersonalGoal): void => {
  if (!outcome) {
    return;
  }
  selectedOutcome.value = outcome.data;
  selectedOutcomeUid.value = outcome.uid ?? "";
  Analytics.action("Clicked Personal Outcome", "PersonalPathwayActions", {
    outcomeTitle: cms.asText(outcome.data.name),
    outcomeUid: outcome.uid,
  });
  outcomeModal.value?.openModal();
};

onMounted(async () => {
  outcomeData.value = await cms.getAll(CmsDocumentTypes.CmsPersonalGoal);
  mapOutcomes(outcomeData.value);
  emit("update-stepper", routeName.value);
  isLoading.value = false;
});
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables";
@import "../../styles/outcomes/select-outcome";
</style>
